/*
 * Opens the cart drawer for add-to-cart flows that don't go through <product-form>.
 *
 * Several custom sections (bundle-products, collection-tabs-products, nootie-before-after,
 * wellness-cross-sell, cheap-products, nx-cross-sell, ...) post to /cart/add with a plain
 * form or their own fetch, so Dawn's drawer never ran - the page either reloaded into
 * /cart or nothing visible happened at all.
 *
 * This mirrors assets/product-form.js exactly: same `sections` / `sections_url` payload,
 * same PUB_SUB_EVENTS.cartUpdate publish, same cart.renderContents(response). Shopify's
 * Web Pixels emit product_added_to_cart for /cart/add.js on their own, so analytics is
 * unaffected.
 *
 * Loaded globally from layout/theme.liquid, so it covers every template - homepage,
 * collections, product, search, blog - without per-section edits.
 */
(function () {
  'use strict';

  function cartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /*
   * This store uses GoKwik's KwikCart side cart (app embed
   * "gokwik-cart-slide-cart-drawer", script kwikcart.gokwik.co/kwikcart/side-cart.js),
   * which is the drawer shoppers are meant to see - not Dawn's.
   *
   * KwikCart only binds itself to add-to-cart buttons matching its own selectors
   * (`form[action='/cart/add'] button[name='add']` and similar), so custom card buttons
   * and plain form posts never triggered it. It exposes these globals, which is how we
   * open it for those flows:
   *   window.refreshSideCart()      - re-reads /cart.js so the drawer shows the new line
   *   window.openGokwikSideCart()   - slides the drawer open
   */
  function openGoKwikSideCart() {
    if (typeof window.openGokwikSideCart !== 'function') return false;
    try {
      if (typeof window.refreshSideCart === 'function') window.refreshSideCart();
      window.openGokwikSideCart();
      return true;
    } catch (error) {
      console.error('[cart] GoKwik side cart failed to open:', error);
      return false;
    }
  }

  /*
   * Single debounced entry point for opening the drawer. Several things can announce the
   * same add (our own submit handler, Dawn's cartUpdate event, the network interceptor
   * below), and KwikCart binds some buttons itself - the flag collapses those into one
   * open instead of several.
   */
  var openScheduled = false;
  function scheduleDrawerOpen() {
    if (openScheduled) return;
    openScheduled = true;

    var fire = function () {
      openScheduled = false;
      openGoKwikSideCart();
    };

    // Dawn keeps the quick-add modal open until the add resolves; opening the drawer
    // underneath it would be invisible, so wait for the modal to close first.
    if (document.querySelector('quick-add-modal[open]')) {
      document.body.addEventListener(
        'modalClosed',
        function () {
          setTimeout(fire, 50);
        },
        { once: true }
      );
      return;
    }

    setTimeout(fire, 150);
  }

  /*
   * Catch-all: watch the network for successful /cart/add calls, whoever makes them.
   *
   * Card add-to-cart comes from too many places to hook individually - Dawn's
   * <product-form>, KwikCart's own button binding (it claims
   * `form[action='/cart/add'] button[name='add']`, which is exactly what a single-variant
   * card renders), and assorted custom section scripts. Whichever one wins the click, the
   * add still goes over the wire, so this is the one reliable signal.
   *
   * Both wrappers are pass-through: the original response is returned untouched.
   */
  function watchCartAddRequests() {
    var isCartAdd = function (url) {
      return typeof url === 'string' && url.indexOf('/cart/add') !== -1;
    };

    if (typeof window.fetch === 'function' && !window.fetch.__nootieWrapped) {
      var originalFetch = window.fetch;
      var wrappedFetch = function (input, init) {
        var url = typeof input === 'string' ? input : input && input.url;
        return originalFetch.apply(this, arguments).then(function (response) {
          if (isCartAdd(url) && response && response.ok) scheduleDrawerOpen();
          return response;
        });
      };
      wrappedFetch.__nootieWrapped = true;
      window.fetch = wrappedFetch;
    }

    var XHR = window.XMLHttpRequest;
    if (XHR && XHR.prototype && !XHR.prototype.__nootieWrapped) {
      var originalOpen = XHR.prototype.open;
      XHR.prototype.open = function (method, url) {
        this.__nootieCartAdd = isCartAdd(url);
        return originalOpen.apply(this, arguments);
      };
      XHR.prototype.addEventListener &&
        (function () {
          var originalSend = XHR.prototype.send;
          XHR.prototype.send = function () {
            var xhr = this;
            if (xhr.__nootieCartAdd) {
              xhr.addEventListener('load', function () {
                if (xhr.status >= 200 && xhr.status < 300) scheduleDrawerOpen();
              });
            }
            return originalSend.apply(this, arguments);
          };
        })();
      XHR.prototype.__nootieWrapped = true;
    }
  }

  watchCartAddRequests();

  /* Hands the response to whichever cart UI this store actually uses. */
  function renderCart(response) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer-global',
        cartData: response,
      });
    }

    // GoKwik's drawer wins when the app is present; Dawn's is the fallback.
    if (typeof window.openGokwikSideCart === 'function') {
      scheduleDrawerOpen();
      return;
    }

    var cart = cartElement();
    if (!cart) {
      // No drawer of either kind: fall back to the cart page rather than doing nothing.
      window.location = (window.routes && window.routes.cart_url) || rootUrl() + 'cart';
      return;
    }

    if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
    cart.renderContents(response);
  }

  function addToCart(body, isFormData) {
    var cart = cartElement();
    var payload = body;

    if (cart && isFormData) {
      payload.append(
        'sections',
        cart.getSectionsToRender().map(function (section) {
          return section.id;
        })
      );
      payload.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    var config =
      typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    if (isFormData) {
      delete config.headers['Content-Type'];
      config.body = payload;
    } else {
      // JSON path: sections have to travel in the body instead of as form fields.
      config.headers['Content-Type'] = 'application/json';
      if (cart) {
        payload.sections = cart.getSectionsToRender().map(function (section) {
          return section.id;
        });
        payload.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }
      config.body = JSON.stringify(payload);
    }

    return fetch(rootUrl() + 'cart/add.js', config).then(function (response) {
      return response.json();
    });
  }

  /*
   * Exposed so sections with their own fetch (cheap-products, nx-cross-sell) can swap a
   * `window.location.href = '/cart'` redirect for a drawer without duplicating any of this.
   */
  window.NootieCart = {
    add: function (variantId, quantity) {
      return addToCart({ id: variantId, quantity: quantity || 1 }, false).then(function (response) {
        if (response.status) throw new Error(response.description || response.message || 'Add to cart failed');
        renderCart(response);
        return response;
      });
    },
    renderCart: renderCart,
  };

  /*
   * Delegated so it survives any section re-render. Forms inside <product-form> are skipped:
   * Dawn already handles those, and handling them here too would add the item twice.
   */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;

      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;
      if (form.closest('product-form')) return;
      if (form.hasAttribute('data-skip-drawer')) return;

      event.preventDefault();

      var submitButton = form.querySelector('[type="submit"]');
      if (submitButton) {
        if (submitButton.getAttribute('aria-disabled') === 'true') return;
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');
      }

      addToCart(new FormData(form), true)
        .then(function (response) {
          if (response.status) {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'cart-drawer-global',
                errors: response.errors || response.description,
                message: response.message,
              });
            }
            console.error('[cart] add failed:', response.description || response.message);
            return;
          }
          renderCart(response);
        })
        .catch(function (error) {
          console.error('[cart] add failed:', error);
        })
        .finally(function () {
          if (submitButton) {
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('loading');
          }
        });
    },
    false
  );

  /*
   * Adds that go through Dawn's own <product-form> - the PDP form and, crucially, the
   * quick-add modal opened by "Choose options" on collection and home cards - are skipped
   * by the submit handler above so nothing double-adds. KwikCart doesn't catch them
   * either: it binds to `form[action='/cart/add'] button[name='add']`, and a card's
   * "Choose options" button sits outside any form, so collection pages have nothing for
   * it to bind to at all (0 matching forms, verified on the live storefront).
   *
   * Listening to Dawn's own cartUpdate event covers all of those flows without modifying
   * product-form.js.
   */
  function watchDawnCartUpdates() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var source = event && event.data ? event.data.source : event && event.source;
      // Our own adds already scheduled an open inside renderCart().
      if (source === 'cart-drawer-global') return;

      // Shares the debounce with the network interceptor, so an add seen by both
      // still opens the drawer exactly once.
      scheduleDrawerOpen();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDawnCartUpdates);
  } else {
    watchDawnCartUpdates();
  }
})();
